// This code comes from an example repo provided by the authors of Webpack's Module Federation (MIT License)
// https://github.com/module-federation/module-federation-examples/tree/master/advanced-api/dynamic-remotes

/* eslint-disable @typescript-eslint/ban-ts-comment */

import { FederatedModule } from 'admin-web-utils'
import React, { useEffect, useState } from 'react'

function loadModule(scope: string, module: string) {
  return async () => {
    // Initializes the share scope. This fills it with known provided modules from this build and all remotes
    // @ts-ignore
    await __webpack_init_sharing__('default')
    // @ts-ignore
    const container = window[scope] // or get the container somewhere else
    // Initialize the container, it may provide shared modules
    // @ts-ignore
    await container.init(__webpack_share_scopes__.default)
    const factory = await container.get(module)
    const Module = factory()
    return Module
  }
}

const urlCache = new Set()
interface DynamicScriptState {
  ready: boolean
  errorLoading: boolean
}

const useDynamicScript = (url: string): DynamicScriptState => {
  const [ready, setReady] = React.useState(false)
  const [errorLoading, setErrorLoading] = React.useState(false)

  useEffect(() => {
    if (!url) return

    if (urlCache.has(url)) {
      setReady(true)
      setErrorLoading(false)
      return
    }

    setReady(false)
    setErrorLoading(false)

    const element = document.createElement('script')

    element.src = url
    element.type = 'text/javascript'
    element.async = true

    element.onload = () => {
      urlCache.add(url)
      setReady(true)
    }

    element.onerror = () => {
      setReady(false)
      setErrorLoading(true)
    }

    document.head.appendChild(element)

    // Example shows returning a cleanup function that
    // removes the url from urlCache and removes the
    // script element from document.head.
    // This is not currently desirable for our usage
  }, [url])

  return {
    errorLoading,
    ready,
  }
}

const moduleCache = new Map()

export function useFederatedModule(
  remoteUrl: string,
  scope: string,
  module: string,
): [FederatedModule | null, boolean] {
  const key = `${remoteUrl}-${scope}-${module}`
  const [Module, setModule] = useState<FederatedModule | null>(null)
  const remoteEntryUrl = `${remoteUrl}/remoteEntry.js`
  const { ready, errorLoading } = useDynamicScript(remoteEntryUrl)

  useEffect(() => {
    if (Module) setModule(null)
    // Only recalculate when key changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(() => {
    if (moduleCache.has(key)) {
      setModule(moduleCache.get(key))
    } else if (ready && !Module) {
      const loadStuff = async (): Promise<void> => {
        const loadedModule = await loadModule(scope, module)()
        moduleCache.set(key, loadedModule)
        setModule(loadedModule)
      }
      loadStuff().catch((e) => console.error(e))
    }
  }, [Module, ready, key, scope, module])

  return [Module, errorLoading]
}
